
import { Component, Vue } from 'vue-property-decorator'
import authService from '@/services/support/AuthService'
import { GrantType, LoginRequest, LoginResponse, RecoveryReasons, SourceDeviceType } from '../types/Login'
import CookiesService from '@/services/support/CookiesService'
import requestRPCPlatform from '@/services/PlatformRPC'
import router from '@/router'
import { LoginErrorDescriptionsTranslate } from '@/types/ErrorDictionary'
import userInfoStore from '@/store/userInfo'
import { redirectToPlatformIndex, removeJWTValues, revokeTokens } from '@/api'
import DeviceInfo from '@/store/deviceInfo'
import config from '@/config'
import UnlockSessions from '@/components/login/UnlockSessions.vue'
import { COOKIES } from '@/types/Config'
import localStorageUser from '@/store/localStoreUser'
import FillgridService from '@/services/FillgridService'
import LogService from '@/services/support/LogService'
import { APIErrorFormat } from '@/types/Errors'
import { RoleNames } from '@/types/Auth'

@Component({
  components: {
    UnlockSessions
  }
})
export default class Login extends Vue {
  OVERPASS_LICENSES_LIMIT_CODE = 2607 // In case this code change in the response in the API, it must be updated here too.
  bgRandom = 0
  loadingLogin = false
  domain: string = ''
  user: string = ''
  password: string = ''
  feedback: string = ''
  versionLabel: string = ''
  INVALID_PLATFORM_ROLES: string[] = [
    RoleNames.MOBILE
  ]
  showUnlockSessions: boolean = false

  created () {
    this.bgRandom = Math.floor((Math.random() * 5) + 1)
    this.getEnviromentSettings()
    this.autoFillFields()
  }
  mounted () {
    localStorageUser.clearListUsers()
  }
  /**
   * Validate if all the required input were filled
   */
  get filledRequiredCredentials (): boolean {
    return this.domain.length > 0 &&
      this.user.length > 0 &&
      this.password.length > 0
  }
  /**
   * Tries to fill the domain and user field
   * by the cookie 'last-login'
   */
  autoFillFields () {
    let loginCookie = CookiesService.get(COOKIES.LAST_LOGIN)
    if (loginCookie) {
      let loginCookieSplit = loginCookie.split('@')
      this.domain = loginCookieSplit[1]
      this.user = loginCookieSplit[0]
      this.focusById('form-password')
    }
  }

  /**
   * Set the 'value' to the 'loadingLogin' variable
   * @param value to assign to the variable
   */
  setLoadingLogin (value: boolean): void {
    this.loadingLogin = value
  }
  /**
   * Initializes the process to create a new token
   */
  tryLogin () {
    if (this.loadingLogin) return
    this.clearFeedback()
    this.setLoadingLogin(true)
    this.createToken()
  }

  /**
   * Based on the domain, user and password,
   * prepare the token request and make the request.
   */
  createToken () {
    this.prepareTokenRequest(this.domain, this.user, this.password)
      .then((tokenRequest) => {
        this.requestToken(tokenRequest)
      })
      .catch((error) => {
        LogService.error('Error on prepare token request. ' + error)
      })
  }

  /**
   * Requests a token with the user credentials and
   * check if the token is valid (no need reset pwd)
   * @param token to request the token
   */
  requestToken (token: LoginRequest) {
    authService.getDeviceToken(token).then((response) => {
      this.setLastLoginCookie(this.user, this.domain)
      this.validateNeedResetPwd(response)
    }).catch((error) => {
      this.handleError(error)
      LogService.error('Error on request token. ' + error)
    })
  }

  /**
   * Checks if the token does not need to reset pwd and
   * init the user credentials in the store
   * @param token response to validate
   */
  validateNeedResetPwd (token: LoginResponse) {
    if (token && token satisfies LoginResponse) {
      if (token.needResetpwd) {
        const params: { recoveryReason: RecoveryReasons } = { recoveryReason: 'expirationPwd' }
        router.push({ name: 'recovery-password', params })
      } else {
        authService.setAuthCookies(token)
        this.initUserCredentials()
      }
    } else {
      this.setLoadingLogin(false)
      LogService.error('Token doesn\'t have a valid schema. ')
    }
  }

  /**
   * Create the 'last-login' token with the value '<userName>@<domainName>'
   * @param user user name of the user that attempt login
   * @param domain domain name of the user that attempt login
   */
  setLastLoginCookie (user: string, domain: string): void {
    CookiesService.set(
      COOKIES.LAST_LOGIN, // name
      `${user}@${domain}`, // value
      '12h', // expires / age
      '/', // path
      config.appDomain, // domain
      config.isProduction // secure flag
    )
  }

  /**
   * Set the JWT user tokens, initialize the user credentials for the app,
   * validate if the user has a valid role and create the RPC credentials
   */
  initUserCredentials () {
    authService.initAuth()
      .then(() => {
        if (this.hasUserPlatformRole) {
          this.generateRPCCredentialsAndRedirectionToindex()
          FillgridService.clearCustomSearchInLocalStorage()
        } else {
          this.deleteInvalidUserToken()
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.invalid_role)
        }
      }).catch((error) => {
        this.handleError(error)
        LogService.error('Error on initializing user credentials. ' + error)
      })
  }

  /**
   * Handles the case when the user has a invalid role for platform
   * revokes the token created and removes the token cookies
   */
  async deleteInvalidUserToken () {
    await revokeTokens()
    removeJWTValues()
    this.setLoadingLogin(false)
  }

  /**
   * Checks if the user in the store has a valid role for platform.
   */
  get hasUserPlatformRole (): boolean {
    return !this.INVALID_PLATFORM_ROLES.includes(userInfoStore.userInfo.role.name)
  }

  /**
   * Make a request ro create the user credentials for platform.
   * If everything is ok, this redirect to platform
   */
  generateRPCCredentialsAndRedirectionToindex () {
    this.loginByParameters()
      .then(() => {
        // If everything is ok, redirect to platform index
        redirectToPlatformIndex()
      })
      .catch((error) => {
        this.setLoadingLogin(false)
        this.handleError(error)
        this.deleteInvalidUserToken()
        LogService.error('Error on login in platform. ' + error)
      })
  }

  /**
   * Handles the error for the API or another kind during trying login
   * @param error to handle
   */
  handleError (error : any) {
    try {
      const apiError = APIErrorFormat.getInstance(error.response.data)
      let errorCode
      let originalErrorDescription = 'Error'
      let status

      if (apiError.isNewErrorFormat()) {
        errorCode = apiError.errors[0].code
        originalErrorDescription = apiError.errors[0].description
        status = apiError.httpError?.status
      }

      if (errorCode !== undefined) {
        // Errors with a code number
        if (errorCode === 2013) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.over_attemps)
        } else if (errorCode === 2600) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.incorrect_values)
        } else if (errorCode === 2601) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.status_invalid)
        } else if (errorCode === this.OVERPASS_LICENSES_LIMIT_CODE) {
          // Manage the error to enable the unlock sessions. 2607
          this.setFeedbackMessage(
            LoginErrorDescriptionsTranslate.active_license ||
            originalErrorDescription
          )
          this.setShowUnlockSessions(true)
        } else if (errorCode === 2608) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.device_ressetle)
        } else if (errorCode === 2640) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.invalid_role)
        } else if (errorCode === 3247) {
          // Error 3247: You must change your password
          this.setFeedbackMessage('Cambio de contraseña requerido')
          const params: { recoveryReason: RecoveryReasons } = { recoveryReason: 'expirationPwd' }
          this.redirectToRecoveryPassword(params)
        } else if (errorCode === 2016) {
          this.setFeedbackMessage(LoginErrorDescriptionsTranslate.domain_temporarily_closed)
          revokeTokens()
          removeJWTValues()
        } else {
          this.setFeedbackMessage('Problema al iniciar sesión.')
        }
      } else if (status) {
        // General errors by API
        if (status === 500) {
          this.setFeedbackMessage('Problema al iniciar sesión. Internal server error.')
        } else {
          this.setFeedbackMessage('Problema al iniciar sesión. ' + LoginErrorDescriptionsTranslate.undefined_error)
        }
      }
    } catch (error) {
      this.setFeedbackMessage('Problema al iniciar sesión. ' + LoginErrorDescriptionsTranslate.undefined_error)
      LogService.error('Error on handleError. ' + error)
    } finally {
      this.setLoadingLogin(false)
    }
  }

  /**
   * Prepares a login request with the login form values
   *
   * @param domain the domain of the user
   * @param user the name of the user
   * @param password the password of the user
   * @returns LoginRequest with the values to create tokens
   */
  async prepareTokenRequest (domain: string, user: string, password: string) {
    try {
      let loginData = new LoginRequest()
      loginData.domain = domain
      loginData.user = user
      loginData.password = password
      loginData.uuid = await DeviceInfo.getFingerprint()
      loginData.device = await DeviceInfo.getBrowserName()
      loginData.grantType = GrantType.PASSWORD_DEVICE
      loginData.sourceDevice = SourceDeviceType.WEB
      return loginData
    } catch (error) {
      LogService.error('Error on prepare token request. ' + error)
      return Promise.reject(error)
    }
  }

  /**
   * Sets the content to the login feedback
   *
   * @param msg to assign
   */
  setFeedbackMessage (msg: string) {
    this.feedback = msg
  }
  /**
   * Gets the enviroment variables by platform API
   * and assign the class to body element
   */
  async getEnviromentSettings () {
    await requestRPCPlatform('getEnvironmentSettings', [])
      .then((response: any) => {
        let body = document.querySelector('body')
        this.versionLabel = 'BMP Versión 3.1.0 - Revisión ' + response.revisionVerion
        if (response.environmentName === 'PROD') {
          body!.classList.add('prod')
        } else if (response.environmentName === 'STAGE') {
          body!.classList.add('stg')
        } else if (response.environmentName === 'DEV') {
          body!.classList.add('dev')
        }
      })
      .catch((e) => {
        LogService.error('Error on getting settings env. ' + e)
      })
  }
  /**
   * Calls the loginByParamenters method from platform api
   * and it generates the required cookies to login in platform.
   */
  async loginByParameters () {
    return requestRPCPlatform(
      'loginByParameters',
      [this.domain, this.user, this.password]
    ).then(() => {
      return Promise.resolve()
    }).catch((e) => {
      LogService.error('Error on login into platform. ' + e)
      return Promise.reject(e)
    })
  }
  /** Redirects to recovery password view */
  redirectToRecoveryPassword (params?: { recoveryReason: RecoveryReasons }) {
    router.push({ name: 'recovery-password', params })
  }
  /** Detects when the key down is enter */
  keydownEnter (event: any) {
    event.preventDefault()
    return event.key === 'Enter'
  }
  /**
   * Focus the 'name' input
   * @param name - name input to focus
  */
  focusById (name: string) {
    let element = document.getElementById(name)
    if (element) element.focus()
  }
  /** Focus the user input */
  toUser (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('form-user')
    }
  }
  /** Focus the password input */
  toPassword (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('form-password')
    }
  }
  /** Focus the login button */
  toLoginButton (event: any) {
    if (this.keydownEnter(event)) {
      this.focusById('button-login')
    }
  }
  /** Clears the feedback message */
  clearFeedback () {
    this.setFeedbackMessage('')
  }
  /**
   * Set a new 'value' to the 'showUnlockSessions' variable
   *
   * @param value to set
   */
  setShowUnlockSessions (value: boolean): void {
    this.showUnlockSessions = value
  }
  /**
   * Close the unlock sessions component when recieve the emit from it
   */
  closeUnlockSessions (): void {
    this.setShowUnlockSessions(false)
    this.setFeedbackMessage('')
  }
}
